import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { db, storage } from '../../services/firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const AdminProfile = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [profileData, setProfileData] = useState({
    displayName: '',
    email: '',
    phoneNumber: '',
    businessName: '',
    businessAddress: '',
    businessHours: '',
    bio: '',
    profilePicture: ''
  });
  
  // Google Calendar Integration
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [connectingCalendar, setConnectingCalendar] = useState(false);
  
  // Profile Picture Upload
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (currentUser) {
      loadProfileData();
      checkCalendarConnection();
    }
  }, [currentUser]);

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  const loadProfileData = async () => {
    if (!currentUser) return;

    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setProfileData({
          displayName: userData.displayName || currentUser.displayName || '',
          email: userData.email || currentUser.email || '',
          phoneNumber: userData.phoneNumber || '',
          businessName: userData.businessName || 'Paradise Landscaping',
          businessAddress: userData.businessAddress || '',
          businessHours: userData.businessHours || '9:00 AM - 5:00 PM',
          bio: userData.bio || 'Professional landscaping administrator',
          profilePicture: userData.profilePicture || ''
        });
      } else {
        // Set defaults from currentUser
        setProfileData(prev => ({
          ...prev,
          displayName: currentUser.displayName || '',
          email: currentUser.email || ''
        }));
      }
    } catch (error) {
      console.error('Error loading profile data:', error);
      showNotification('Failed to load profile data.', 'error');
    }
  };

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveProfile = async () => {
    if (!currentUser) return;

    setLoading(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        ...profileData,
        updatedAt: new Date()
      });
      
      showNotification('Profile updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating profile:', error);
      showNotification('Failed to update profile. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Profile Picture Upload
  const handleProfilePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handleProfilePictureChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showNotification('Please select a valid image file.', 'error');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showNotification('Image size must be less than 5MB.', 'error');
      return;
    }

    setUploadingPicture(true);
    try {
      // Delete old profile picture if exists
      if (profileData.profilePicture) {
        try {
          const oldImageRef = ref(storage, `profile-pictures/${currentUser.uid}`);
          await deleteObject(oldImageRef);
        } catch (deleteError) {
          console.warn('Could not delete old profile picture:', deleteError);
        }
      }

      // Upload new profile picture
      const imageRef = ref(storage, `profile-pictures/${currentUser.uid}`);
      await uploadBytes(imageRef, file);
      const downloadURL = await getDownloadURL(imageRef);

      // Update profile data
      const updatedProfileData = {
        ...profileData,
        profilePicture: downloadURL
      };
      
      setProfileData(updatedProfileData);
      
      // Save to Firestore
      await updateDoc(doc(db, 'users', currentUser.uid), {
        profilePicture: downloadURL,
        updatedAt: new Date()
      });

      showNotification('Profile picture updated successfully!', 'success');
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      showNotification('Failed to upload profile picture. Please try again.', 'error');
    } finally {
      setUploadingPicture(false);
    }
  };

  // Google Calendar Integration
  const checkCalendarConnection = () => {
    // Check if user has connected Google Calendar (you can store this in user document)
    // For now, we'll assume it's not connected
    setCalendarConnected(false);
  };

  const connectGoogleCalendar = async () => {
    setConnectingCalendar(true);
    
    try {
      // Initialize Google API
      if (!window.gapi) {
        showNotification('Google API not loaded. Please refresh the page.', 'error');
        return;
      }

      await new Promise((resolve) => {
        window.gapi.load('auth2', resolve);
      });

      const authInstance = window.gapi.auth2.init({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        scope: 'https://www.googleapis.com/auth/calendar'
      });

      const user = await authInstance.signIn();
      const accessToken = user.getAuthResponse().access_token;

      // Save calendar connection to user profile
      await updateDoc(doc(db, 'users', currentUser.uid), {
        googleCalendarConnected: true,
        googleCalendarToken: accessToken,
        updatedAt: new Date()
      });

      setCalendarConnected(true);
      showNotification('Google Calendar connected successfully!', 'success');
    } catch (error) {
      console.error('Error connecting Google Calendar:', error);
      showNotification('Failed to connect Google Calendar. Please try again.', 'error');
    } finally {
      setConnectingCalendar(false);
    }
  };

  const disconnectGoogleCalendar = async () => {
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        googleCalendarConnected: false,
        googleCalendarToken: null,
        updatedAt: new Date()
      });

      setCalendarConnected(false);
      showNotification('Google Calendar disconnected successfully.', 'success');
    } catch (error) {
      console.error('Error disconnecting Google Calendar:', error);
      showNotification('Failed to disconnect Google Calendar.', 'error');
    }
  };

  return (
    <Layout>
      <div className="admin-profile-container" style={{ 
        padding: windowWidth <= 768 ? '0.5rem' : '1rem 2rem',
        maxWidth: '100%',
        overflowX: 'hidden',
        minHeight: '100vh',
        boxSizing: 'border-box'
      }}>
        {/* Toast Notification */}
        {notification && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: windowWidth <= 768 ? '10px' : '20px',
            left: windowWidth <= 768 ? '10px' : 'auto',
            zIndex: 1000,
            maxWidth: windowWidth <= 768 ? 'calc(100vw - 20px)' : '400px',
            padding: windowWidth <= 768 ? '0.75rem 1rem' : '1rem 1.5rem',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            backgroundColor: notification.type === 'success' ? '#4caf50' : '#f44336',
            color: 'white',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}>
            {notification.message}
          </div>
        )}

        {/* Header */}
        <div style={{ marginBottom: windowWidth <= 768 ? '1rem' : '2rem' }}>
          <h1 style={{ 
            fontSize: windowWidth <= 768 ? '1.5rem' : '2rem', 
            fontWeight: 'bold', 
            color: '#333', 
            margin: 0 
          }}>
            Profile
          </h1>
          <p style={{ 
            color: '#666', 
            marginTop: '0.5rem',
            fontSize: windowWidth <= 768 ? '0.875rem' : '1rem'
          }}>
            Manage your profile information and settings
          </p>
        </div>

        <div className="admin-profile-grid" style={{ 
          display: windowWidth <= 768 ? 'block' : 'grid', 
          gridTemplateColumns: windowWidth <= 768 ? 'none' : '1fr 2fr', 
          gap: windowWidth <= 768 ? '0' : '2rem' 
        }}>
          {/* Left Column - Profile Picture & Basic Info */}
          <div>
            {/* Profile Picture Section */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: windowWidth <= 768 ? '1.5rem' : '2rem',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
              textAlign: 'center',
              marginBottom: windowWidth <= 768 ? '1rem' : '2rem'
            }}>
              <div style={{ position: 'relative', display: 'inline-block', marginBottom: '1.5rem' }}>
                <div
                  onClick={handleProfilePictureClick}
                  style={{
                    width: windowWidth <= 768 ? '100px' : '120px',
                    height: windowWidth <= 768 ? '100px' : '120px',
                    borderRadius: '50%',
                    backgroundColor: profileData.profilePicture ? 'transparent' : '#6366f1',
                    backgroundImage: profileData.profilePicture ? `url(${profileData.profilePicture})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    border: '4px solid #e5e7eb',
                    fontSize: '3rem',
                    color: 'white',
                    fontWeight: 'bold',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {uploadingPicture ? (
                    <div style={{ fontSize: '1.5rem' }}>⏳</div>
                  ) : profileData.profilePicture ? (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0,
                      transition: 'opacity 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = 1}
                    onMouseLeave={(e) => e.target.style.opacity = 0}
                    >
                      📷
                    </div>
                  ) : (
                    profileData.displayName?.charAt(0) || 'P'
                  )}
                </div>
                
                <div style={{
                  position: 'absolute',
                  bottom: '5px',
                  right: '5px',
                  backgroundColor: '#6366f1',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  border: '2px solid white',
                  fontSize: '0.875rem'
                }}
                onClick={handleProfilePictureClick}
                >
                  📷
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleProfilePictureChange}
                style={{ display: 'none' }}
              />

              <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', fontWeight: '600', color: '#1f2937' }}>
                {profileData.displayName || 'Pablo'}
              </h2>
              
              <div style={{
                backgroundColor: '#10b981',
                color: 'white',
                padding: '0.25rem 0.75rem',
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: '600',
                display: 'inline-block',
                marginBottom: '1rem'
              }}>
                Administrator
              </div>

              <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>
                {profileData.bio}
              </p>

              <div style={{ marginTop: '1.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <span>📧</span>
                  <span>{profileData.email}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <span>🕒</span>
                  <span>{profileData.businessHours}</span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: windowWidth <= 768 ? '1rem' : '1.5rem',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.125rem', fontWeight: '600', color: '#1f2937' }}>
                Quick Actions
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <button
                  onClick={() => window.location.href = '/admin/appointments'}
                  style={{
                    background: '#f3f4f6',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    padding: '0.75rem 1rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  📅 View Appointments
                </button>
                
                <button
                  onClick={() => window.location.href = '/admin/support'}
                  style={{
                    background: '#f3f4f6',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    padding: '0.75rem 1rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  🎫 Support Tickets
                </button>
                
                <button
                  onClick={() => window.location.href = '/admin/estimates'}
                  style={{
                    background: '#f3f4f6',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    padding: '0.75rem 1rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  💰 Estimates
                </button>
              </div>
            </div>
          </div>

          {/* Right Column - Profile Information & Settings */}
          <div style={{ marginTop: windowWidth <= 768 ? '1rem' : '0' }}>
            {/* Profile Information */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: windowWidth <= 768 ? '1.5rem' : '2rem',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
              marginBottom: windowWidth <= 768 ? '1rem' : '2rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
                <span style={{ fontSize: '1.25rem' }}>👤</span>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#1f2937' }}>
                  Profile Information
                </h3>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: windowWidth <= 768 ? '1fr' : '1fr 1fr', 
                gap: '1rem', 
                marginBottom: '1rem' 
              }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={profileData.displayName}
                    onChange={(e) => handleInputChange('displayName', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={profileData.email}
                    disabled
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      backgroundColor: '#f9fafb',
                      color: '#6b7280'
                    }}
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.25rem 0 0 0' }}>
                    Email cannot be changed
                  </p>
                </div>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: windowWidth <= 768 ? '1fr' : '1fr 1fr', 
                gap: '1rem', 
                marginBottom: '1rem' 
              }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={profileData.phoneNumber}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                    placeholder="(555) 123-4567"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    Business Name
                  </label>
                  <input
                    type="text"
                    value={profileData.businessName}
                    onChange={(e) => handleInputChange('businessName', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                  Business Address
                </label>
                <input
                  type="text"
                  value={profileData.businessAddress}
                  onChange={(e) => handleInputChange('businessAddress', e.target.value)}
                  placeholder="123 Main St, City, State 12345"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                  Business Hours
                </label>
                <input
                  type="text"
                  value={profileData.businessHours}
                  onChange={(e) => handleInputChange('businessHours', e.target.value)}
                  placeholder="9:00 AM - 5:00 PM"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                  Bio / Description
                </label>
                <textarea
                  value={profileData.bio}
                  onChange={(e) => handleInputChange('bio', e.target.value)}
                  rows="3"
                  placeholder="Professional landscaping administrator..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              <button
                onClick={handleSaveProfile}
                disabled={loading}
                style={{
                  background: loading ? '#9ca3af' : '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: windowWidth <= 768 ? '0.75rem 1.5rem' : '0.75rem 2rem',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  width: windowWidth <= 768 ? '100%' : 'auto'
                }}
              >
                {loading ? 'Saving...' : 'Save Profile'}
              </button>
            </div>

            {/* Google Calendar Integration */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: windowWidth <= 768 ? '1.5rem' : '2rem',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
              marginBottom: windowWidth <= 768 ? '1rem' : '2rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <span style={{ fontSize: '1.25rem' }}>📅</span>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#1f2937' }}>
                  Google Calendar Integration
                </h3>
              </div>

              <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
                Connect your Google Calendar to automatically sync appointments and manage your schedule.
              </p>

              {calendarConnected ? (
                <div style={{
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span style={{ color: '#16a34a', fontSize: '1.25rem' }}>✅</span>
                    <span style={{ color: '#15803d', fontWeight: '600', fontSize: '0.875rem' }}>
                      Google Calendar Connected
                    </span>
                  </div>
                  <p style={{ color: '#166534', fontSize: '0.875rem', margin: 0 }}>
                    Your appointments will automatically sync with Google Calendar.
                  </p>
                </div>
              ) : (
                <div style={{
                  background: '#fef3c7',
                  border: '1px solid #fde68a',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span style={{ color: '#d97706', fontSize: '1.25rem' }}>⚠️</span>
                    <span style={{ color: '#92400e', fontWeight: '600', fontSize: '0.875rem' }}>
                      Calendar Not Connected
                    </span>
                  </div>
                  <p style={{ color: '#a16207', fontSize: '0.875rem', margin: 0 }}>
                    Connect your Google Calendar to enable automatic appointment syncing.
                  </p>
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                {calendarConnected ? (
                  <button
                    onClick={disconnectGoogleCalendar}
                    style={{
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.75rem 1.5rem',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '600'
                    }}
                  >
                    Disconnect Calendar
                  </button>
                ) : (
                  <button
                    onClick={connectGoogleCalendar}
                    disabled={connectingCalendar}
                    style={{
                      background: connectingCalendar ? '#9ca3af' : '#4285f4',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.75rem 1.5rem',
                      cursor: connectingCalendar ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <span style={{ fontSize: '1rem' }}>📅</span>
                    {connectingCalendar ? 'Connecting...' : 'Connect Google Calendar'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Load Google API */}
      <script src="https://apis.google.com/js/api.js"></script>
    </Layout>
  );
};

export default AdminProfile;
